import React from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme, alpha } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InboxIcon from '@mui/icons-material/Inbox';
import ForumIcon from '@mui/icons-material/Forum';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useComposeTabsStore } from '../../stores/composeTabsStore';

const GlobalComposeTabs: React.FC = () => {
  console.log('[GlobalComposeTabs] Component rendering');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { tabs, activeTabId, setActiveTab, closeTab, createTab, showingCompose, chatOpen, viewingChat, openChat, closeChat, setViewingChat, docsOpen, viewingDocs, closeDocs, setViewingDocs, sheetsOpen, viewingSheets, closeSheets, setViewingSheets } = useComposeTabsStore();

  // Debug logging
  React.useEffect(() => {
    console.log('[GlobalComposeTabs] chatOpen:', chatOpen, 'viewingChat:', viewingChat, 'showingCompose:', showingCompose, 'tabs:', tabs.length);
  }, [chatOpen, viewingChat, showingCompose, tabs.length]);

  const isOnMailPage = location.pathname.startsWith('/mail');

  // Don't show if nothing to show
  if (tabs.length === 0 && !isOnMailPage && !chatOpen && !docsOpen && !sheetsOpen) {
    return null;
  }

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleMailTabClick = () => {
    if (showingCompose) {
      // hide compose, go back to mail view without navigating
      useComposeTabsStore.getState().setShowingCompose(false);
    }
    // Switch to mail view without closing chat tab
    setViewingChat(false);
    setViewingDocs(false);
    setViewingSheets(false);
    navigate('/mail/inbox');
  };

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    closeTab(tabId);
  };

  const handleAddTab = () => {
    createTab();
  };

  const getTabTitle = (tab: typeof tabs[0]) => {
    if (tab.subject?.trim()) {
      return tab.subject.length > 22 ? tab.subject.substring(0, 22) + '…' : tab.subject;
    }
    return 'No Subject';
  };

  // Has unsaved content?
  const hasContent = (tab: typeof tabs[0]) =>
    tab.to.length > 0 || tab.subject.trim() || tab.body.replace(/<[^>]*>/g, '').trim();

  // Colors — always light/white regardless of app theme
  const bgStrip = '#f0f2f5';
  const tabActive = '#ffffff';
  const tabHover = 'rgba(0,0,0,0.05)';
  const tabBorder = 'rgba(0,0,0,0.1)';
  const textActive = theme.palette.primary.main;
  const textIdle = '#5f6368';

  // Shared tab style factory — Mail tab and compose tabs are identical in size/shape
  const tabSx = (isActive: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.75,
    py: 0,
    height: 36,
    minWidth: 130,
    maxWidth: 200,
    cursor: 'pointer',
    userSelect: 'none' as const,
    bgcolor: isActive ? tabActive : 'transparent',
    borderRadius: '8px 8px 0 0',
    border: isActive ? `1px solid ${tabBorder}` : '1px solid transparent',
    borderBottom: isActive ? `1px solid ${tabActive}` : '1px solid transparent',
    mb: isActive ? '-1px' : 0,
    transition: 'background 0.12s ease',
    '&:hover': { bgcolor: isActive ? tabActive : tabHover },
    mr: 0.25,
    flexShrink: 0,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        bgcolor: bgStrip,
        borderBottom: `1px solid ${tabBorder}`,
        minHeight: 42,
        px: 1,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Mail / Inbox tab */}
      <Box onClick={handleMailTabClick} sx={tabSx(!showingCompose && (!chatOpen || !viewingChat))}>
        <InboxIcon sx={{ fontSize: 15, color: (!showingCompose && (!chatOpen || !viewingChat)) ? textActive : textIdle, flexShrink: 0 }} />
        <Typography
          noWrap
          sx={{
            fontSize: '0.8125rem',
            fontWeight: (!showingCompose && (!chatOpen || !viewingChat)) ? 600 : 500,
            color: (!showingCompose && (!chatOpen || !viewingChat)) ? textActive : textIdle,
            flex: 1,
            letterSpacing: '-0.01em',
          }}
        >
          Mail
        </Typography>
        {/* Spacer to match close-button width on compose tabs */}
        <Box sx={{ width: 20, flexShrink: 0 }} />
      </Box>

      {/* Team Chat tab — only shown when chat is open */}
      {chatOpen && (
        <Box onClick={() => { setViewingChat(true); }} sx={tabSx(viewingChat && !showingCompose)}>
          <ForumIcon sx={{ fontSize: 14, color: (viewingChat && !showingCompose) ? textActive : textIdle, flexShrink: 0 }} />
          <Typography
            noWrap
            sx={{
              fontSize: '0.8125rem',
              fontWeight: (viewingChat && !showingCompose) ? 600 : 500,
              color: (viewingChat && !showingCompose) ? textActive : textIdle,
              flex: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Team Chat
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); closeChat(); }}
            sx={{
              p: 0.2,
              flexShrink: 0,
              color: textIdle,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.08)', color: '#202124' },
            }}
          >
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      )}

      {/* Documents tab */}
      {docsOpen && (() => {
        const active = viewingDocs && !showingCompose;
        const docColor = '#1a73e8';
        return (
          <Box onClick={() => setViewingDocs(true)} sx={tabSx(active)}>
            <DescriptionIcon sx={{ fontSize: 14, color: active ? docColor : textIdle, flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 500, color: active ? docColor : textIdle, flex: 1, letterSpacing: '-0.01em' }}>
              Documents
            </Typography>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); closeDocs(); }} sx={{ p: 0.2, flexShrink: 0, color: textIdle, '&:hover': { bgcolor: 'rgba(0,0,0,0.08)', color: '#202124' } }}>
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        );
      })()}

      {/* Spreadsheets tab */}
      {sheetsOpen && (() => {
        const active = viewingSheets && !showingCompose;
        const sheetColor = '#0f9d58';
        return (
          <Box onClick={() => setViewingSheets(true)} sx={tabSx(active)}>
            <TableChartIcon sx={{ fontSize: 14, color: active ? sheetColor : textIdle, flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 500, color: active ? sheetColor : textIdle, flex: 1, letterSpacing: '-0.01em' }}>
              Spreadsheets
            </Typography>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); closeSheets(); }} sx={{ p: 0.2, flexShrink: 0, color: textIdle, '&:hover': { bgcolor: 'rgba(0,0,0,0.08)', color: '#202124' } }}>
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        );
      })()}

      {/* Compose Tabs */}
      {tabs.map((tab) => {
        const isActive = showingCompose && activeTabId === tab.id;
        const dirty = hasContent(tab);
        return (
          <Tooltip key={tab.id} title={tab.subject?.trim() || 'No Subject'} placement="bottom" arrow>
            <Box onClick={() => handleTabClick(tab.id)} sx={tabSx(isActive)}>
              {/* Left icon: unsaved dot or edit icon */}
              {dirty && !isActive
                ? <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.primary.main, flexShrink: 0 }} />
                : <EditIcon sx={{ fontSize: 14, color: isActive ? textActive : textIdle, flexShrink: 0 }} />
              }
              <Typography
                noWrap
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? textActive : textIdle,
                  flex: 1,
                  letterSpacing: '-0.01em',
                }}
              >
                {getTabTitle(tab)}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => handleCloseTab(e, tab.id)}
                sx={{
                  p: 0.2,
                  flexShrink: 0,
                  color: textIdle,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)', color: '#202124' },
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          </Tooltip>
        );
      })}

      {/* New compose button */}
      <Tooltip title="New compose" placement="bottom" arrow>
        <IconButton
          size="small"
          onClick={handleAddTab}
          sx={{
            ml: 0.5,
            mb: 0.5,
            p: 0.5,
            color: textIdle,
            '&:hover': { bgcolor: tabHover, color: '#202124' },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default GlobalComposeTabs;
